import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyCheck,
    faMapLocationDot,
    faEnvelope,
    faFileUser,
} from '@fortawesome/pro-regular-svg-icons';
import {
    POL_THEME,
    MCO_THEME,
    ThemeContext,
} from '../../constants/theme';
import themeStyles from '../../_theme.scss';
import styles from './Advertise.scss';
import Button from '../shared/Button/Button';
import { Titles } from '../../constants/titles';
import { AppContext } from '../../context/AppContext';
import { Descriptions } from '../../constants/Descriptions';

// Background images
const POL_ADVERTISE_IMG = `${process.env.CUSTOMER_DATA_S3_URL}/advertise/pol_advertise_bg.jpg`;
const MCO_ADVERTISE_IMG = `${process.env.CUSTOMER_DATA_S3_URL}/advertise/mco_advertise_bg.jpg`;

const Advertise = () => {
    const themeContext = useContext(ThemeContext);
    const { setPageTitle, setPageDescription } = useContext(AppContext);

    useEffect(() => {
        switch (themeContext) {
            case MCO_THEME:
                setPageTitle(Titles.MCO_ADVERTISERS);
                setPageDescription(Descriptions.MCO_ADVERTISERS);
                break;
            case POL_THEME:
                setPageTitle(Titles.POL_ADVERTISERS);
                setPageDescription(Descriptions.POL_ADVERTISERS);
                break;
            default:
                break;
        }
    }, [themeContext, setPageTitle, setPageDescription]);

    let headerImgStyle = '';
    let iconColor = '';
    let imageHeadlineCopy = '';
    let couponCopy = '';
    let geoTargetCopy = '';
    let pubAlertsSection = '';
    let adVisbilityCopy = '';

    if (themeContext === POL_THEME) {
        headerImgStyle = styles.polHeaderImg;
        iconColor = styles.colorPolIcon;
        imageHeadlineCopy = (
            <h5 className={styles.panelPrimary}>
                Business owners: Support your local church while connecting
                with consumers by advertising on Parishes Online.
            </h5>
        );
        couponCopy = (
            <p>
                Create your own coupons. Offer specials anytime you want to
                encourage parishioners to check out your products and services.
            </p>
        );
        geoTargetCopy = (
            <p>
                Advertise directly on a church listing page wherever
                you do business. Target specific zip codes, cities, or states.
            </p>
        );
        pubAlertsSection = (
            <div className={styles.featureItemTextWrapper}>
                <h4>Weekly Bulletin Emails</h4>
                <p>
                    We increase the value of your printed church bulletin advertisement,
                    through weekly email distribution of the church’s bulletin online.
                </p>
            </div>
        );
        adVisbilityCopy = (
            <p>
                Have your ad seen by local parishioners in your neighborhood and town!
            </p>
        );
    } else {
        headerImgStyle = styles.mcoHeaderImg;
        iconColor = styles.colorMcoIcon;
        imageHeadlineCopy = (
            <h5 className={styles.panelPrimary}>
                Business owners, support your local community by
                advertising on My Community Online.
            </h5>
        );
        couponCopy = (
            <p>
                Create your own coupons. Offer specials anytime you
                want visitors to check out your products and services.
            </p>
        );
        geoTargetCopy = (
            <p>
                Advertise directly on a community page wherever
                you do business. Target specific zip codes, cities, or states.
            </p>
        );
        pubAlertsSection = (
            <div className={styles.featureItemTextWrapper}>
                <h4>New Publication Email Alerts</h4>
                <p>
                    We drive traffic back to your advertisement through
                    email notices sent to community members.
                </p>
            </div>
        );
        adVisbilityCopy = (
            <p>
                Have your ad seen by people in your neighborhood and town!
            </p>
        );
    }

    return (
        <div className={themeStyles.outerContainer}>
            <main className={styles.container}>
                <header className={styles.headerWrapper}>
                    <h1 className={`${styles.primaryHeader} ${themeStyles.headerText}`}>Advertise with us</h1>
                    <div className={styles.learnMoreWrapper}>
                        Questions about advertising?
                        <a
                            href="https://www.4lpi.com/contact-us"
                            rel="noreferrer"
                            target="_blank"
                        >
                            <Button size="small">
                                Contact us
                            </Button>
                        </a>
                    </div>
                </header>

                <section
                    className={classNames(styles.mainPanel, headerImgStyle)}
                    style={{
                        backgroundImage: themeContext === POL_THEME
                            ? `url('${POL_ADVERTISE_IMG}')`
                            : `url('${MCO_ADVERTISE_IMG}')`,
                    }}
                >
                    <div className={styles.panelTextWrapper}>
                        {imageHeadlineCopy}
                    </div>
                </section>

                <h3 className={styles.featuresHeader}>By advertising with us, you&apos;ll receive these and more!</h3>

                <section className={styles.featuresContainer}>

                    <div className={styles.featureRow}>
                        <div className={styles.featureWrapper}>
                            <FontAwesomeIcon
                                size="3x"
                                icon={faFileUser}
                                className={classNames(styles.icon, iconColor)}
                            />
                            <div className={styles.featureItemTextWrapper}>
                                <h4>Dedicated Listing Page</h4>
                                <p>
                                    Your advertisement comes with your own dedicated
                                    listing page where you can showcase your business and
                                    link to your website and Facebook page.
                                </p>
                            </div>
                        </div>

                        <div className={styles.featureWrapper}>
                            <FontAwesomeIcon
                                size="3x"
                                icon={faMoneyCheck}
                                className={classNames(styles.icon, iconColor)}
                            />
                            <div className={styles.featureItemTextWrapper}>
                                <h4>Coupon Creator</h4>
                                {couponCopy}
                            </div>
                        </div>
                    </div>

                    <div className={styles.featureRow}>
                        <div className={styles.featureWrapper}>
                            <FontAwesomeIcon
                                size="3x"
                                icon={faMapLocationDot}
                                className={classNames(styles.icon, iconColor)}
                            />
                            <div className={styles.featureItemTextWrapper}>
                                <h4>Geo-Target Your Ad</h4>
                                {geoTargetCopy}
                            </div>
                        </div>

                        <div className={styles.featureWrapper}>
                            <FontAwesomeIcon
                                size="3x"
                                icon={faEnvelope}
                                className={classNames(styles.icon, iconColor)}
                            />
                            {pubAlertsSection}
                        </div>
                    </div>
                </section>
                <section className={styles.contactContainer}>
                    <div className="small-12 columns callout center">
                        <h4>Simple, fast, and easy</h4>
                        {adVisbilityCopy}
                        <a
                            rel="noreferrer"
                            target="_blank"
                            href="https://www.4lpi.com/contact-us/"
                        >
                            <Button size="medium">
                                Contact us
                            </Button>
                        </a>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Advertise;
